<template>
  <div>
    <div v-if="!homePage" class="exhibition-content">

      <div class="main-container">
        <h1 id="exhibition-title">{{ name }}</h1>
        <img v-if="resources[0]" class="header-image" :src="resourceUrls[0]" alt="Header Image" />

        <section class="resources-section">
          <div v-for="(resource, index) in resources" :key="index" class="resource-card">
            <div class="media-wrap">
              <img v-if="isImage(resource.name)" :src="resourceUrls[index]" alt="Resource Image" class="media" />
              <video v-else-if="isVideo(resource.name)" :src="resourceUrls[index]" controls class="media"></video>
              <div class="media-overlay"></div>
              <div class="media-badge">{{ index + 1 }}</div>
              <div class="media-title">{{ resource.title }}</div>
            </div>
            <div class="resource-meta">
              <p class="resource-description">{{ resource.description }}</p>
            </div>
          </div>
        </section>

        <footer class="exhibition-footer">
          <span>2021 ALPA TECHNOLOGIES</span>
          <span>Mentions Legales</span>
          <span>Fattu in Corsica</span>
        </footer>
      </div>
    </div>
    <div class="home-section" v-else>
      <component :is="selectedTemplate" :homeImage="homeImage" @exit="exitHomePage"></component>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import axios from "axios";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import Modele_2 from "../../components/Models/Modele_2.vue";
import Modele_3 from "../../components/Models/Modele_3.vue";
import Modele_1 from "../../components/Models/Modele_1.vue";
import Modele_4 from "../../components/Models/Modele_4.vue";
import Modele_5 from "../../components/Models/Modele_5.vue";
import Modele_6 from "../../components/Models/Modele_6.vue";
import Modele_7 from "../../components/Models/Modele_7.vue";
import { useRoute } from "vue-router";
gsap.registerPlugin(ScrollTrigger);

export default {
  layout: "default",
  components: {
    Modele_2,
    Modele_3,
    Modele_1,
    Modele_4,
    Modele_5,
    Modele_6,
    Modele_7
  },
  props: [
    'homePageProps',
    'exhibitId'
  ],
  setup(props) {
    const route = useRoute();
    const id = ref(route.params.id ?? props.exhibitId);
    const resources = ref([]);
    const name = ref("");
    const userName = ref("");
    const headImage = ref([]);
    const selectedTemplate = ref("");
    const homePage = ref(props.homePageProps ?? true);
    const homeImage = ref({});
    const resourceUrls = ref([])

    const getHeadImage = (index) => {
      let url = `${process.env.VUE_APP_BASEURL}/s3Resource/${resources.value[index].name}`;
      return url;
    };

    const fetchResources = async () => {
      if (resources.value.length === 0) {
        // Attendre que les ressources soient chargées si nécessaire
        await getResources();
      }

      // Utiliser Promise.all pour attendre que toutes les requêtes soient terminées
      const urls = resources.value.map(resource => `${process.env.VUE_APP_BASEURL}/s3Resource/${resource.name}`);
      try {
        const responses = await Promise.all(urls.map(url => axios.get(url)));
        resourceUrls.value = responses.map(response => response.data);
      } catch (error) {
        console.error("Erreur lors de la récupération des URLs des ressources", error);
      }
    }

    const getUserName = async () => {
      let url = `${process.env.VUE_APP_BASEURL}/user/${id.value}`;
      let response = await axios.get(url);
      userName.value = response.data.name;
    };

    const getHomeImage = async () => {
      try {
        let url = `${process.env.VUE_APP_BASEURL}/exhibit_home/${id.value}`;
        let response = await axios.get(url);
        homeImage.value = response.data[0];
      } catch (error) {
        console.error(error);
      }
    };

    const getModel = async () => {
      try {
        let url = `${process.env.VUE_APP_BASEURL}/model/${id.value}`;
        let response = await axios.get(url);
        if (response.data.length > 0) {
          selectedTemplate.value = response.data[0].model;
          selectedTemplate.value = selectedTemplate.value.replace(/è/g, 'e').replace(/\s+/g, '_');
        }
      } catch (error) {
        console.error(error);
      }
    };

    const exitHomePage = () => {
      homePage.value = false;
    };

    const getExhibitName = async () => {
      let url = `${process.env.VUE_APP_BASEURL}/exhibit/${id.value}`;
      let response = await axios.get(url);
      name.value = response.data.exhibit_name;
    };


    const getResources = async () => {
      let exhibitId = id.value;

      try {
        // Obtenir les ressources de l'exposition
        let resourcesUrl = `${process.env.VUE_APP_BASEURL}/resources`;
        let response = await axios.post(resourcesUrl, { exhibitId });
        let allResources = response.data;

        // Obtenir les resource_id de gallery_resource
        let galleryUrl = `${process.env.VUE_APP_BASEURL}/galleryResources/${exhibitId}`;
        let galleryResponse = await axios.get(galleryUrl);
        let galleryResourceIds = galleryResponse.data.map(res => res.resource_id);

        // Filtrer les ressources pour ne garder que celles présentes dans gallery_resource
        resources.value = allResources.filter(resource =>
          galleryResourceIds.includes(resource.id)
        );
      } catch (error) {
        console.error("Error fetching resources:", error);
      }
    };


    onMounted(() => {
      getHomeImage();
      getResources();
      fetchResources();
      getExhibitName();
      getUserName();
      getModel();
    });



    const isImage = (fileName) => {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
    };

    const isVideo = (fileName) => {
      return /\.(mp4|mov|avi)$/i.test(fileName);
    };


    return {
      resources,
      name,
      userName,
      headImage,
      selectedTemplate,
      homePage,
      homeImage,
      resourceUrls,
      getHeadImage,
      exitHomePage,
      isImage,
      isVideo
    };
  }
};
</script>

<style scoped>
/* Design moderne avec focus sur la typographie, les espacements et les transitions */
:root {
  --bg: #0b1020;
  --card: #0f172a;
  --muted: #cbd5e1; /* plus lisible */
  --text: #f8fafc;  /* texte principal très lisible */
  --accent1: #7c3aed;
  --accent2: #22d3ee;
  --glow: 0 10px 30px rgba(124, 58, 237, 0.35);
}

.exhibition-content {
  min-height: 100vh;
  background:
    radial-gradient(800px 300px at 10% 10%, rgba(124,58,237,.25), transparent 60%),
    radial-gradient(600px 200px at 90% 20%, rgba(34,211,238,.20), transparent 60%),
    linear-gradient(180deg, #0b1020 0%, #121a33 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.main-container {
  color: var(--text);
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 48px 24px 64px;
}

#exhibition-title {
  font-size: clamp(2rem, 4vw, 3rem);
  text-transform: uppercase;
  letter-spacing: .12em;
  text-align: center;
  margin: 0 0 24px;
  color: #ffffff; /* haute lisibilité */
  text-shadow: 0 3px 14px rgba(0,0,0,.6), 0 1px 6px rgba(0,0,0,.45);
}

.header-image {
  width: min(100%, 1100px);
  aspect-ratio: 21/9;
  object-fit: cover;
  border-radius: 18px;
  box-shadow: var(--glow);
  margin: 0 auto 48px;
  display: block;
}

.resources-section {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 28px;
}

.resource-card {
  position: relative;
  background: linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.06));
  border: 1px solid rgba(148,163,184,0.28);
  border-radius: 16px;
  overflow: hidden;
  transition: transform .35s ease, box-shadow .35s ease, border-color .35s ease;
  backdrop-filter: blur(6px);
}
.resource-card:hover {
  transform: translateY(-6px);
  border-color: rgba(124,58,237,0.65);
  box-shadow: var(--glow);
}

.media-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  overflow: hidden;
}
.media {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.02);
  transition: transform .6s ease;
}
.resource-card:hover .media { transform: scale(1.08); }

.media-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(11,16,32,.92) 0%, rgba(11,16,32,.6) 50%, rgba(11,16,32,.0) 85%);
  pointer-events: none;
}
.media-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: linear-gradient(135deg, var(--accent1), var(--accent2));
  color: white;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: .08em;
  padding: 6px 10px;
  border-radius: 999px;
  box-shadow: 0 6px 16px rgba(34,211,238,.25);
}
.media-title {
  position: absolute;
  bottom: 12px;
  left: 12px;
  color: #ffffff;
  font-weight: 800;
  font-size: clamp(1rem, 2.2vw, 1.15rem);
  text-shadow: 0 2px 14px rgba(0,0,0,.6), 0 1px 6px rgba(0,0,0,.4);
  background: rgba(0,0,0,.78);
  padding: 6px 10px;
  border-radius: 8px;
  display: inline-block;
  max-width: 85%;
}

.resource-meta {
  padding: 16px 16px 18px;
  background: rgba(15,23,42,0.96); /* fond très lisible */
  border-top: 1px solid rgba(148,163,184,0.35);
}
.resource-description {
  color: #ffffff;
  line-height: 1.7;
  font-size: 1rem;
  margin: 0;
}

.exhibition-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  opacity: .8;
  margin-top: 48px;
  font-size: .95rem;
}

/* Small screens */
@media (max-width: 640px) {
  .header-image { aspect-ratio: 16/9; }
  .resources-section { grid-template-columns: 1fr; gap: 20px; }
}
</style>
  
